import React, { useState, useMemo, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Users,
  Gift,
  ClipboardList,
  Phone,
  Stethoscope,
  Baby,
  Cog,
  Sparkle,
  Target,
  FileHeart,
  Smile,
  Brain,
  HeartPulse,
  Ear,
  UserRound,
  Bone,
  Apple,
  HandHeart,
  X,
  ChevronLeft,
  ChevronRight,
  Camera,
  Eye,
  Zap,
  Shield,
  Plus,
  Minus,
} from "lucide-react";
import { Button } from "../components/ui/button";
import img1 from "../img/galery/6.jpeg";
import img2 from "../img/galery/10.jpeg";
import clinicalCases1 from "../img/cases/case1.jpg";
import clinicalCases3 from "../img/cases/case3.jpeg";
import clinicalCases4 from "../img/cases/case4.jpeg";
import clinicalCases5 from "../img/cases/case5.jpeg";
import clinicalCases6 from "../img/cases/case6.jpeg";
import clinicalCases6_1 from "../img/cases/case6.1.jpeg";
import clinicalCases7 from "../img/cases/case7.jpeg";
import clinicalCases7_1 from "../img/cases/case7.1.jpeg";
import clinicalCases8 from "../img/cases/case8.jpeg";
import clinicalCases9 from "../img/cases/case9.jpeg";
import clinicalCases10 from "../img/cases/case10.jpeg";
import clinicalCases11 from "../img/cases/case11.jpeg";
import clinicalCases12 from "../img/cases/case12.jpeg";
import clinicalCases13 from "../img/cases/case13.jpeg";
import clinicalCases14 from "../img/cases/case14.jpeg";
import clinicalCases15 from "../img/cases/case15.jpeg";
import clinicalCases16 from "../img/cases/case16.jpeg";
import clinicalCases17 from "../img/cases/case17.jpeg";
import clinicalCases18 from "../img/cases/case18.jpeg";
import clinicalCases19 from "../img/cases/case19.jpeg";
import clinicalCases20 from "../img/cases/case20.jpeg";
import clinicalCases21 from "../img/cases/case21.jpeg";
import clinicalCases22 from "../img/cases/case22.jpeg";
import clinicalCases23 from "../img/cases/cases23.jpg";
import nutri from "../img/cases/nutri.png";

// All images that need to be preloaded per service
const SERVICE_IMAGES = {
  orthodontics: [
    clinicalCases8,
    clinicalCases9,
    clinicalCases10,
    clinicalCases11,
    clinicalCases12,
    clinicalCases13,
    clinicalCases15,
    clinicalCases16,
  ],
  "oral-surgery": [],
  prosthetics: [clinicalCases1, clinicalCases5],
  implantology: [
    clinicalCases6,
    clinicalCases6_1,
    clinicalCases7,
    clinicalCases7_1,
  ],
  tmj: [clinicalCases14],
  "aesthetic-dentistry": [
    clinicalCases3,
    clinicalCases17,
    clinicalCases18,
    clinicalCases19,
    clinicalCases20,
  ],
  "orofacial-harmonization": [clinicalCases23],
  nutrition: [nutri],
};

const HERO_IMAGES = {
  "general-dentistry": img1,
  "pediatric-dentistry": img1,
  prosthetics: img1,
  "aesthetic-dentistry": img1,
  implantology: img1,
  "oral-surgery": img1,
  endodontics: img1,
  orthodontics: img1,
  tmj: img1,
  periodontics: img1,
  nutrition: nutri,
};

function preloadImages(srcs) {
  return Promise.all(
    srcs.map(
      (src) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = resolve; // resolve even on error so we don't block
          img.src = src;
        }),
    ),
  );
}

const ServiceDetailPage = () => {
  const { t } = useTranslation();
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const allServices = [
    "general-dentistry",
    "pediatric-dentistry",
    "prosthetics",
    "aesthetic-dentistry",
    "implantology",
    "oral-surgery",
    "endodontics",
    "orthodontics",
    "tmj",
    "periodontics",
    "orofacial-harmonization",
    "ent",
    "general-medicine",
    "orthopedics",
    "nutrition",
    "massage",
  ];

  const generalDentistryServices = [
    "general-dentistry",
    "pediatric-dentistry",
    "prosthetics",
    "aesthetic-dentistry",
    "implantology",
    "oral-surgery",
    "endodontics",
    "orthodontics",
    "tmj",
    "periodontics",
  ];

  const heroBackground =
    serviceId === "nutrition"
      ? nutri
      : generalDentistryServices.includes(serviceId)
        ? img1
        : img2;

  // Preload all relevant images before showing the page
  useEffect(() => {
    setImagesLoaded(false);

    const serviceSpecific = SERVICE_IMAGES[serviceId] || [];
    const toLoad = [heroBackground, ...serviceSpecific].filter(Boolean);

    preloadImages(toLoad).then(() => {
      setImagesLoaded(true);
    });
  }, [serviceId, heroBackground]);

  const serviceIcons = {
    "general-dentistry": Stethoscope,
    "pediatric-dentistry": Baby,
    prosthetics: Cog,
    "aesthetic-dentistry": Sparkle,
    implantology: Shield,
    "oral-surgery": Target,
    endodontics: FileHeart,
    orthodontics: Smile,
    tmj: Brain,
    periodontics: HeartPulse,
    "orofacial-harmonization": Sparkle,
    ent: Ear,
    "general-medicine": UserRound,
    orthopedics: Bone,
    nutrition: Apple,
    massage: HandHeart,
  };

  const dentalServices = [
    "general-dentistry",
    "pediatric-dentistry",
    "prosthetics",
    "aesthetic-dentistry",
    "implantology",
    "oral-surgery",
    "endodontics",
    "orthodontics",
    "tmj",
    "periodontics",
  ];

  const relatedServices = allServices
    .filter((s) => s !== serviceId)
    .slice(0, 3);

  const benefits = t(`services.items.${serviceId}.benefits`, {
    returnObjects: true,
  });

  const IconComponent = serviceIcons[serviceId];

  const openImageModal = (images, index) => {
    setSelectedImage(images);
    setCurrentImageIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedImage) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedImage.length);
    }
  };

  const prevImage = () => {
    if (selectedImage) {
      setCurrentImageIndex(
        (prev) => (prev - 1 + selectedImage.length) % selectedImage.length,
      );
    }
  };

  // ========== ORTODONTIA ==========
  const renderOrthodonticsSection = () => {
    if (serviceId !== "orthodontics") return null;

    const orthodonticSections = [
      {
        title: t("services.orthodontics.conventional.title"),
        text: t("services.orthodontics.conventional.text"),
        indicatedForTitle: t(
          "services.orthodontics.conventional.indicatedForTitle",
        ),
        indicatedFor: t("services.orthodontics.conventional.indicatedFor"),
        expectTitle: t("services.orthodontics.conventional.expectTitle"),
        expect: t("services.orthodontics.conventional.expect"),
        benefitsTitle: t("services.orthodontics.conventional.benefitsTitle"),
        benefits: t("services.orthodontics.conventional.benefits", {
          returnObjects: true,
        }),
        image: clinicalCases16,
        alt: "Aparelhos convencionais",
      },
      {
        title: t("services.orthodontics.aligners.title"),
        text: t("services.orthodontics.aligners.text"),
        indicatedForTitle: t(
          "services.orthodontics.aligners.indicatedForTitle",
        ),
        indicatedFor: t("services.orthodontics.aligners.indicatedFor"),
        expectTitle: t("services.orthodontics.aligners.expectTitle"),
        expect: t("services.orthodontics.aligners.expect"),
        benefitsTitle: t("services.orthodontics.aligners.benefitsTitle"),
        benefits: t("services.orthodontics.aligners.benefits", {
          returnObjects: true,
        }),
        image: clinicalCases15,
        alt: "Alinhadores invisíveis",
      },
    ];

    const clinicalCases = [
      {
        id: 1,
        images: [
          { url: clinicalCases12, caption: "Antes" },
          { url: clinicalCases11, caption: "Depois" },
        ],
      },
      {
        id: 2,
        images: [
          { url: clinicalCases10, caption: "Antes" },
          { url: clinicalCases13, caption: "Depois" },
        ],
      },
      {
        id: 3,
        images: [
          { url: clinicalCases9, caption: "Antes" },
          { url: clinicalCases8, caption: "Depois" },
        ],
      },
    ];

    return (
      <>
        <section className="py-20 bg-beige-dark/30 relative">
          <div className="container-luxury relative">
            <div className="text-center mb-12">
              <Camera className="w-8 h-8 text-primary mx-auto mb-4" />
              <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
                {t("services.resultsGallery")}
              </span>
              <h2 className="text-title font-serif text-foreground">
                {t("services.clinicalCases")}
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                {t("services.orthodontics.casesDescription")}
              </p>
            </div>

            <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-6">
              {clinicalCases.map((caseItem) => (
                <div
                  key={caseItem.id}
                  className="card-luxury rounded-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 max-w-2xl mx-auto"
                >
                  <div className="flex flex-col">
                    {caseItem.images.map((img, idx) => (
                      <div
                        key={idx}
                        className="aspect-[16/10] relative cursor-pointer"
                        onClick={() => openImageModal(caseItem.images, idx)}
                      >
                        <img
                          src={img.url}
                          alt={img.caption}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0">
                          <span className="absolute bottom-2 left-2 text-white text-xs font-semibold px-2 py-1 bg-primary/80 rounded">
                            {img.caption}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-b from-primary/5 via-primary/10 to-primary/5 relative">
          <div className="container-luxury relative">
            <div className="space-y-16">
              {orthodonticSections.map((section, index) => (
                <div
                  key={index}
                  className="grid lg:grid-cols-2 gap-10 items-center"
                >
                  <div className={index % 2 !== 0 ? "lg:order-2" : ""}>
                    <h3 className="text-2xl font-serif text-foreground mb-4">
                      {section.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {section.text}
                    </p>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-serif text-foreground mb-2">
                          {section.indicatedForTitle}
                        </h4>
                        <p className="text-muted-foreground">
                          {section.indicatedFor}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-serif text-foreground mb-2">
                          {section.expectTitle}
                        </h4>
                        <p className="text-muted-foreground">
                          {section.expect}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-serif text-foreground mb-2">
                          {section.benefitsTitle}
                        </h4>
                        <ul className="space-y-2">
                          {Array.isArray(section.benefits) &&
                            section.benefits.map((benefit, idx) => (
                              <li
                                key={idx}
                                className="flex items-center gap-2 text-muted-foreground"
                              >
                                <Check className="w-4 h-4 text-primary" />
                                <span>{benefit}</span>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className={index % 2 !== 0 ? "lg:order-1" : ""}>
                    <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
                      <img
                        src={section.image}
                        alt={section.alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </>
    );
  };

  // ========== CIRURGIA ORAL ==========
  const renderOralSurgerySection = () => {
    if (serviceId !== "oral-surgery") return null;

    return (
      <section className="py-20 bg-gradient-to-b from-background to-beige-dark/20 relative">
        <div className="container-luxury relative">
          <div className="grid lg:grid-cols gap-12 items-start">
            <div className="space-y-8">
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8">
                <h3 className="text-xl font-serif text-foreground mb-4">
                  {t("services.oralSurgery.whatIs")}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t("services.oralSurgery.whatIsDesc")}
                </p>
              </div>
              <div>
                <h3 className="text-xl font-serif text-foreground mb-6 flex items-center gap-3">
                  <Users className="w-5 h-5 text-primary" />
                  {t("services.whoIsItFor")}
                </h3>
                <ul className="space-y-3">
                  {[
                    t("services.oralSurgery.indications.cysts"),
                    t("services.oralSurgery.indications.mucosaLesions"),
                    t("services.oralSurgery.indications.teethExtraction"),
                    t("services.oralSurgery.indications.fibromas"),
                    t("services.oralSurgery.indications.prostheticSurgery"),
                    t("services.oralSurgery.indications.gingivoplasty"),
                    t("services.oralSurgery.indications.gingivalRetraction"),
                  ].map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-center gap-3 text-muted-foreground"
                    >
                      <Check className="w-5 h-5 text-primary flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  // ========== PRÓTESES ==========
  const renderProstheticsSection = () => {
    if (serviceId !== "prosthetics") return null;

    const fixedImages = [{ url: clinicalCases5 }];
    const removableImages = [{ url: clinicalCases1 }];

    return (
      <section className="py-20 bg-background relative">
        <div className="container-luxury relative">
          <div className="mb-20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <Cog className="w-6 h-6 text-primary" />
              </div>
              <div>
                <span className="text-primary text-sm font-medium tracking-wider uppercase">
                  {t("services.permanentSolutions")}
                </span>
                <h2 className="text-title font-serif text-foreground">
                  {t("services.prosthetics.fixedTitle")}
                </h2>
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {t("services.prosthetics.fixedDesc")}
                </p>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                  <h4 className="font-serif text-foreground mb-4">
                    {t("services.advantages")}:
                  </h4>
                  <ul className="space-y-2">
                    {(
                      t("services.prosthetics.fixedAdvantages", {
                        returnObjects: true,
                      }) || []
                    ).map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-2 text-muted-foreground text-sm"
                      >
                        <Check className="w-4 h-4 text-primary" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="flex justify-center">
                {fixedImages.map((img, idx) => (
                  <div
                    key={idx}
                    className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group shadow-lg hover:scale-105 transition-transform"
                    onClick={() => openImageModal(fixedImages, idx)}
                  >
                    <img
                      src={img.url}
                      alt={img.caption}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-medium text-center px-2">
                        {img.caption}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-border my-12" />

          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <Cog className="w-6 h-6 text-primary" />
              </div>
              <div>
                <span className="text-primary text-sm font-medium tracking-wider uppercase">
                  {t("services.flexibleSolutions")}
                </span>
                <h2 className="text-title font-serif text-foreground">
                  {t("services.prosthetics.removableTitle")}
                </h2>
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 flex justify-center">
                {removableImages.map((img, idx) => (
                  <div
                    key={idx}
                    className="relative w-full max-w-sm h-[500px] rounded-xl overflow-hidden cursor-pointer group shadow-lg hover:scale-105 transition-transform"
                    onClick={() => openImageModal(removableImages, idx)}
                  >
                    <img
                      src={img.url}
                      alt={img.caption}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-medium text-center px-2">
                        {img.caption}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {t("services.prosthetics.removableDesc")}
                </p>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                  <h4 className="font-serif text-foreground mb-4">
                    {t("services.advantages")}:
                  </h4>
                  <ul className="space-y-2">
                    {(
                      t("services.prosthetics.removableAdvantages", {
                        returnObjects: true,
                      }) || []
                    ).map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-2 text-muted-foreground text-sm"
                      >
                        <Check className="w-4 h-4 text-primary" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  // ========== IMPLANTOLOGIA ==========
  const renderImplantologySection = () => {
    if (serviceId !== "implantology") return null;

    const implantImage = {
      url: "https://primadent.pt/wp-content/uploads/2025/09/implante-unico-e1758038758205.jpg",
    };

    return (
      <>
        <section className="py-20 bg-gradient-to-b from-background to-primary/5 relative">
          <div className="container-luxury relative">
            <div className="text-center mb-16">
              <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
                {t("services.cuttingEdgeTechnology")}
              </span>
              <h2 className="text-display font-serif text-foreground mb-6">
                {t("services.implantology.whatAreImplants")}
              </h2>
            </div>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {t("services.implantology.whatAreImplantsDesc")}
                </p>
                <div className="bg-primary/10 border border-primary/20 rounded-2xl p-8">
                  <h4 className="font-serif text-foreground mb-4 text-xl">
                    {t("services.implantology.biocompatibility")}
                  </h4>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("services.implantology.biocompatibilityDesc")}
                  </p>
                </div>
                <div className="bg-beige-dark/50 rounded-2xl p-8">
                  <h4 className="font-serif text-foreground mb-4 text-xl">
                    {t("services.implantology.objective")}
                  </h4>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("services.implantology.objectiveDesc")}
                  </p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden w-full h-auto">
                <img
                  src={implantImage.url}
                  alt={implantImage.caption}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-beige-dark/30 relative">
          <div className="container-luxury relative">
            <div className="text-center mb-16">
              <h2 className="text-title font-serif text-foreground mb-4">
                {t("services.implantology.indications")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t("services.implantology.indicationsDesc")}
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  url: "https://primadent.pt/wp-content/uploads/2025/09/266-d8fb546e-250499c1-1920w-1000x667-1920w.webp",
                  title: t("services.implantology.oneTooth"),
                  desc: t("services.implantology.oneToothDesc"),
                },
                {
                  url: "https://primadent.pt/wp-content/uploads/2025/09/055-1920w-1000x666-1920w.webp",
                  title: t("services.implantology.severalTeeth"),
                  desc: t("services.implantology.severalTeethDesc"),
                },
                {
                  url: "https://primadent.pt/wp-content/uploads/2025/09/427-1920w-1000x667-1920w.webp",
                  title: t("services.implantology.allTeeth"),
                  desc: t("services.implantology.allTeethDesc"),
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="bg-background rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="aspect-video relative">
                    <img
                      src={item.url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h4 className="font-serif text-foreground text-lg mb-3">
                      {item.title}
                    </h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-background relative">
          <div className="container-luxury relative">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-title font-serif text-foreground mb-12">
                {t("services.implantology.advantages")}
              </h2>
              <div className="space-y-4">
                {(
                  t("services.implantology.advantagesList", {
                    returnObjects: true,
                  }) || []
                ).map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-4 bg-primary/5 border border-primary/10 rounded-xl p-4"
                  >
                    <Check className="w-6 h-6 text-primary flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-beige-dark/20 relative">
          <div className="container-luxury relative">
            <div className="text-center mb-12">
              <Camera className="w-8 h-8 text-primary mx-auto mb-4" />
              <h2 className="text-title font-serif text-foreground mb-4">
                {t("services.clinicalCases")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t("services.implantology.casesDescription")}
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {[
                {
                  title: t("services.implantology.case1Title"),
                  desc: t("services.implantology.case1Desc"),
                  images: [
                    { url: clinicalCases6_1, caption: t("services.before") },
                    { url: clinicalCases6, caption: t("services.after") },
                  ],
                },
                {
                  title: t("services.implantology.case2Title"),
                  desc: t("services.implantology.case2Desc"),
                  images: [
                    { url: clinicalCases7_1, caption: t("services.before") },
                    { url: clinicalCases7, caption: t("services.after") },
                  ],
                },
              ].map((caseItem, idx) => (
                <div
                  key={idx}
                  className="card-luxury rounded-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 w-full"
                >
                  <div className="flex flex-col h-[400px]">
                    {caseItem.images.map((img, imgIdx) => (
                      <div
                        key={imgIdx}
                        className="h-1/2 relative cursor-pointer"
                        onClick={() => openImageModal(caseItem.images, imgIdx)}
                      >
                        <img
                          src={img.url}
                          alt={img.caption}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0">
                          <span className="absolute bottom-2 left-2 text-white text-xs font-semibold px-2 py-1 bg-primary/80 rounded">
                            {img.caption}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </>
    );
  };

  // ========== ESTÉTICA DENTÁRIA ==========
  const renderAestheticDentistrySection = () => {
    if (serviceId !== "aesthetic-dentistry") return null;

    const aestheticBenefits =
      t("services.items.aesthetic-dentistry.benefits", {
        returnObjects: true,
      }) || [];

    const aestheticCases = [
      {
        images: [
          { url: clinicalCases17, caption: t("services.before") },
          { url: clinicalCases18, caption: t("services.after") },
        ],
      },
      {
        images: [
          { url: clinicalCases19, caption: t("services.before") },
          { url: clinicalCases20, caption: t("services.after") },
        ],
      },
    ];

    return (
      <>
        <section className="py-20 bg-gradient-to-b from-background to-primary/5 relative">
          <div className="container-luxury relative">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="card-luxury rounded-2xl p-8">
                <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-serif text-foreground mb-4">
                  {t("services.whoIsItFor")}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t("services.items.aesthetic-dentistry.whoIsItFor")}
                </p>
              </div>

              <div className="card-luxury rounded-2xl p-8">
                <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                  <Gift className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-serif text-foreground mb-4">
                  {t("services.benefits")}
                </h3>
                <ul className="space-y-3">
                  {Array.isArray(aestheticBenefits) &&
                    aestheticBenefits.map((benefit, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-muted-foreground text-sm"
                      >
                        <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                </ul>
              </div>

              <div className="card-luxury rounded-2xl p-8">
                <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                  <ClipboardList className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-serif text-foreground mb-4">
                  {t("services.whatToExpect")}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t("services.items.aesthetic-dentistry.whatToExpect")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-beige-dark/20 relative">
          <div className="container-luxury relative">
            <div className="mb-10">
              <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[16/7]">
                <img
                  src={clinicalCases3}
                  alt={t("services.items.aesthetic-dentistry.title")}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              </div>
            </div>

            <div className="text-center mb-12">
              <Camera className="w-8 h-8 text-primary mx-auto mb-4" />
              <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
                {t("services.resultsGallery")}
              </span>
              <h2 className="text-title font-serif text-foreground">
                {t("services.clinicalCases")}
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                {t("services.items.aesthetic-dentistry.casesDescription")}
              </p>
            </div>

            <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-6 justify-items-center">
              {aestheticCases.map((caseItem, index) => (
                <div
                  key={`${serviceId}-${index}`}
                  className="bg-background rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 w-full max-w-sm"
                >
                  <div className="flex flex-col">
                    {caseItem.images.map((img, imgIdx) => (
                      <div
                        key={imgIdx}
                        className="aspect-[16/10] relative cursor-pointer"
                        onClick={() => openImageModal(caseItem.images, imgIdx)}
                      >
                        <img
                          src={img.url}
                          alt={img.caption}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0">
                          <span className="absolute bottom-2 left-2 text-white text-xs font-semibold px-2 py-1 bg-primary/80 rounded">
                            {img.caption}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </>
    );
  };

  // ========== OCLUSÃO (TMJ) ==========
  const renderTmjSection = () => {
    if (serviceId !== "tmj") return null;

    const symptoms = t("services.tmj.symptoms", { returnObjects: true }) || [];

    const treatments = [
      {
        title: t("services.tmj.treatments.splints.title"),
        desc: t("services.tmj.treatments.splints.desc"),
      },
      {
        title: t("services.tmj.treatments.physiotherapy.title"),
        desc: t("services.tmj.treatments.physiotherapy.desc"),
      },
      {
        title: t("services.tmj.treatments.medication.title"),
        desc: t("services.tmj.treatments.medication.desc"),
      },
      {
        title: t("services.tmj.treatments.occlusalAdjustment.title"),
        desc: t("services.tmj.treatments.occlusalAdjustment.desc"),
      },
    ];

    return (
      <section className="py-20 bg-gradient-to-b from-background to-beige-dark/20 relative">
        <div className="container-luxury relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Brain className="w-6 h-6 text-primary" />
                <span className="text-primary text-sm font-medium tracking-wider uppercase">
                  {t("services.tmj.understandDTM")}
                </span>
              </div>
              <h2 className="text-display font-serif text-foreground mb-6">
                {t("services.tmj.orofacialPain")}
              </h2>
              <p className="text-muted-foreground leading-relaxed text-lg mb-6">
                {t("services.tmj.description")}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t("services.tmj.painDescription")}
              </p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl ml-10">
              <img
                src={clinicalCases14}
                alt={t("services.tmj.orofacialPain")}
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent" />
            </div>
          </div>

          <div className="mb-20">
            <h3 className="text-title font-serif text-foreground mb-8 text-center">
              {t("services.tmj.commonSymptoms")}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {symptoms.map((symptom, idx) => (
                <div
                  key={idx}
                  className="bg-background border border-primary/10 rounded-xl p-4 flex items-center gap-3"
                >
                  <Check className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">
                    {symptom}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-title font-serif text-foreground mb-8 text-center">
              {t("services.tmj.treatmentOptions")}
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {treatments.map((treatment, idx) => (
                <div
                  key={idx}
                  className="bg-primary/5 border border-primary/20 rounded-xl p-6 text-center hover:bg-primary/10 transition-colors"
                >
                  <h4 className="font-serif text-foreground mb-2">
                    {treatment.title}
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    {treatment.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  };

  // ========== HARMONIZAÇÃO OROFACIAL ==========
  const renderOrofacialHarmonizationSection = () => {
    if (serviceId !== "orofacial-harmonization") return null;

    const treatments =
      t("services.orofacialHarmonization.treatments", {
        returnObjects: true,
      }) || [];

    const benefits =
      t("services.items.orofacial-harmonization.benefits", {
        returnObjects: true,
      }) || [];

    return (
      <>
        <section className="py-20 bg-gradient-to-b from-background to-primary/5 relative">
          <div className="container-luxury relative">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="card-luxury rounded-2xl p-8">
                <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-serif text-foreground mb-4">
                  {t("services.whoIsItFor")}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t("services.items.orofacial-harmonization.whoIsItFor")}
                </p>
              </div>

              <div className="card-luxury rounded-2xl p-8">
                <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                  <Gift className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-serif text-foreground mb-4">
                  {t("services.benefits")}
                </h3>
                <ul className="space-y-3">
                  {Array.isArray(benefits) &&
                    benefits.map((benefit, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-muted-foreground text-sm"
                      >
                        <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                </ul>
              </div>

              <div className="card-luxury rounded-2xl p-8">
                <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                  <ClipboardList className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-serif text-foreground mb-4">
                  {t("services.whatToExpect")}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t("services.items.orofacial-harmonization.whatToExpect")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-beige-dark/20 relative">
          <div className="container-luxury relative">
            <div className="text-center mb-14">
              <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
                {t("services.orofacialHarmonization.label")}
              </span>
              <h2 className="text-title font-serif text-foreground mb-4">
                {t("services.orofacialHarmonization.title")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t("services.orofacialHarmonization.description")}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-10 items-start">
              <div className="space-y-4">
                {treatments.map((treatment, index) => {
                  const isOpen = openAccordion === index;
                  return (
                    <div
                      key={treatment.title}
                      className="rounded-2xl border border-primary/15 bg-background overflow-hidden shadow-sm"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenAccordion(isOpen ? null : index)}
                        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-primary/5 transition-colors"
                      >
                        <span className="text-lg font-serif text-foreground">
                          {treatment.title}
                        </span>
                        <span className="text-primary flex-shrink-0">
                          {isOpen ? (
                            <Minus className="w-5 h-5" />
                          ) : (
                            <Plus className="w-5 h-5" />
                          )}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="px-6 pb-6">
                          <ul className="space-y-3">
                            {treatment.items.map((item, idx) => (
                              <li
                                key={idx}
                                className="flex items-start gap-3 text-muted-foreground text-sm leading-relaxed"
                              >
                                <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="lg:sticky lg:top-24">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/5]">
                  <img
                    src={clinicalCases23}
                    alt={t("services.items.orofacial-harmonization.title")}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  };

  if (!allServices.includes(serviceId)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-headline font-serif text-foreground mb-4">404</h1>
          <p className="text-muted-foreground mb-8">Serviço não encontrado</p>
          <Link to="/services">
            <Button className="btn-luxury text-cream">
              {t("services.backToServices")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Loading screen while images preload
  if (!imagesLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center mx-auto mb-6">
            {IconComponent && (
              <IconComponent className="w-7 h-7 text-primary" />
            )}
          </div>
          <div className="flex items-center gap-2 justify-center text-muted-foreground text-sm">
            <svg
              className="animate-spin w-4 h-4 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
            A carregar...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeImageModal}
        >
          <button
            onClick={closeImageModal}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
          >
            <X className="w-8 h-8" />
          </button>

          {selectedImage.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 text-white/80 hover:text-white p-2"
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 text-white/80 hover:text-white p-2"
              >
                <ChevronRight className="w-10 h-10" />
              </button>
            </>
          )}

          <div
            className="max-w-5xl max-h-[85vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage[currentImageIndex].url}
              alt={selectedImage[currentImageIndex].caption}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
              decoding="async"
            />
            <p className="text-white text-center mt-4 text-lg font-medium">
              {selectedImage[currentImageIndex].caption}
            </p>
            {selectedImage.length > 1 && (
              <p className="text-white/60 text-center mt-2 text-sm">
                {currentImageIndex + 1} / {selectedImage.length}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${heroBackground})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/75 to-background" />
        </div>

        <div className="relative container-luxury pt-32 pb-20">
          <div className="max-w-3xl mx-auto">
            <button
              onClick={() => navigate("/services")}
              className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors"
              data-testid="back-to-services-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("services.backToServices")}
            </button>

            <div className="w-20 h-20 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center mb-6 backdrop-blur-sm">
              {IconComponent && (
                <IconComponent className="w-9 h-9 text-primary" />
              )}
            </div>

            <h1
              className="text-display font-serif text-foreground mb-6"
              data-testid="service-title"
            >
              {t(`services.items.${serviceId}.title`)}
            </h1>

            <p className="text-body-lg text-muted-foreground">
              {t(`services.items.${serviceId}.description`)}
            </p>
          </div>
        </div>
      </section>

      {/* Service-specific sections */}
      {renderOrthodonticsSection()}
      {renderOralSurgerySection()}
      {renderProstheticsSection()}
      {renderImplantologySection()}
      {renderTmjSection()}
      {renderAestheticDentistrySection()}
      {renderOrofacialHarmonizationSection()}

      {/* Standard Content */}
      {![
        "orthodontics",
        "oral-surgery",
        "prosthetics",
        "implantology",
        "tmj",
        "aesthetic-dentistry",
        "orofacial-harmonization",
      ].includes(serviceId) && (
        <section className="section-luxury bg-background relative">
          <div className="container-luxury relative">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="card-luxury rounded-lg p-8">
                <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-serif text-foreground mb-4">
                  {t("services.whoIsItFor")}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t(`services.items.${serviceId}.whoIsItFor`)}
                </p>
              </div>

              <div className="card-luxury rounded-lg p-8">
                <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                  <Gift className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-serif text-foreground mb-4">
                  {t("services.benefits")}
                </h3>
                <ul className="space-y-3">
                  {Array.isArray(benefits) &&
                    benefits.map((benefit, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-muted-foreground text-sm"
                      >
                        <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                </ul>
              </div>

              <div className="card-luxury rounded-lg p-8">
                <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                  <ClipboardList className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-serif text-foreground mb-4">
                  {t("services.whatToExpect")}
                </h3>
                {serviceId === "orthopedics" ? (
                  <ul className="space-y-3">
                    {(
                      t(`services.items.${serviceId}.whatToExpect`, {
                        returnObjects: true,
                      }) || []
                    ).map((item, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-muted-foreground text-sm leading-relaxed"
                      >
                        <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t(`services.items.${serviceId}.whatToExpect`)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {dentalServices.includes(serviceId) && (
        <section className="py-16 bg-beige-dark relative">
          <div className="container-luxury relative">
            <div className="text-center">
              <h2 className="text-title font-serif text-foreground mb-6">
                {t("home.cta.title")}
              </h2>
              <a href="tel:+351253994232">
                <Button
                  className="btn-luxury text-cream px-8 py-4 h-auto text-base font-medium"
                  data-testid="cta-phone-btn"
                >
                  <Phone className="mr-2 w-4 h-4" />
                  253 994 232
                </Button>
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ServiceDetailPage;
